// Custom web components for this theme, kept separate from the base Impact theme's
// assets/theme.js bundle so our own code doesn't get mixed into (or overwritten by) upstream
// theme updates. Loaded as an ES module from layout/theme.liquid alongside theme.js.

// sections/fat-announcement-bar.liquid — mobile carousel behaviour.
// Renders full-width slides in `[data-track]`; on mobile (<1000px) it turns the flat scroll
// track into a true infinite carousel: a clone of the last slide is prepended and a clone of
// the first slide is appended, so `next`/`prev` always scroll in one continuous direction and,
// once the clone is reached, the position is silently reset (no animation) to the matching real
// slide. The clones are hidden on desktop via the `fat-announcement-bar__item--clone` class.
const FatAnnouncementBar = class extends HTMLElement {
  connectedCallback() {
    this._track = this.querySelector("[data-track]");
    this._prevButton = this.querySelector("[data-prev]");
    this._nextButton = this.querySelector("[data-next]");

    const isSlide = (el) => el.classList.contains("fat-announcement-bar__item") && !el.classList.contains("fat-announcement-bar__item--no-mobile");
    this._realItems = Array.from(this._track.children).filter(isSlide);
    this._loopEnabled = this._realItems.length > 1;

    if (this._loopEnabled) {
      this._headClone = this._realItems[this._realItems.length - 1].cloneNode(true);
      this._tailClone = this._realItems[0].cloneNode(true);
      for (const clone of [this._headClone, this._tailClone]) {
        clone.classList.add("fat-announcement-bar__item--clone");
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
        // Strip theme-editor block bindings so the editor's block picker only ever targets the real item.
        for (const el of [clone, ...clone.querySelectorAll("*")]) {
          for (const attr of Array.from(el.attributes)) {
            if (attr.name.startsWith("data-shopify")) el.removeAttribute(attr.name);
          }
        }
      }
      this._track.insertBefore(this._headClone, this._realItems[0]);
      this._track.appendChild(this._tailClone);
    }

    this._slides = Array.from(this._track.children).filter(isSlide);
    this._index = this._loopEnabled ? 1 : 0;
    if (this._loopEnabled) {
      this._track.scrollLeft = this._slides[this._index].offsetLeft;
    }

    this._isVisible = true;
    this._mediaQuery = window.matchMedia("(max-width: 999px)");
    this._reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this._onMediaChange = this._onMediaChange.bind(this);
    this._onIntersectSlide = this._onIntersectSlide.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onScroll = this._onScroll.bind(this);

    if (this._realItems.length < 2) {
      if (this._prevButton) this._prevButton.hidden = true;
      if (this._nextButton) this._nextButton.hidden = true;
    } else {
      if (this._prevButton) this._prevButton.addEventListener("click", () => this._prev(), { signal });
      if (this._nextButton) this._nextButton.addEventListener("click", () => this._next(), { signal });
    }

    this.addEventListener("pointerenter", () => this._stopAutoplay(), { signal });
    this.addEventListener("focusin", () => this._stopAutoplay(), { signal });
    this.addEventListener("pointerleave", () => this._resumeAutoplay(), { signal });
    this.addEventListener("focusout", () => this._resumeAutoplay(), { signal });
    document.addEventListener("visibilitychange", this._onVisibilityChange, { signal });
    this._track.addEventListener("scroll", this._onScroll, { signal, passive: true });

    this._itemObserver = new IntersectionObserver(this._onIntersectSlide, { root: this._track, threshold: 0.6 });
    this._slides.forEach((slide) => this._itemObserver.observe(slide));

    this._visibilityObserver = new IntersectionObserver((entries) => {
      this._isVisible = entries[0].isIntersecting;
      this._isVisible ? this._resumeAutoplay() : this._stopAutoplay();
    });
    this._visibilityObserver.observe(this);

    this._mediaQuery.addEventListener("change", this._onMediaChange, { signal });
    this._onMediaChange();
  }

  disconnectedCallback() {
    this._abortController.abort();
    this._itemObserver?.disconnect();
    this._visibilityObserver?.disconnect();
    window.clearTimeout(this._scrollSettleTimer);
    this._stopAutoplay();
  }

  _onMediaChange() {
    const isMobile = this._mediaQuery.matches;
    if (this._realItems.length > 1) {
      if (this._prevButton) this._prevButton.hidden = !isMobile;
      if (this._nextButton) this._nextButton.hidden = !isMobile;
    }
    if (isMobile) {
      this._track.setAttribute("aria-roledescription", "carousel");
      this._resumeAutoplay();
    } else {
      this._track.removeAttribute("aria-roledescription");
      this._stopAutoplay();
    }
  }

  _onIntersectSlide(entries) {
    const visibleEntry = entries.find((entry) => entry.isIntersecting);
    if (!visibleEntry) return;
    const index = this._slides.indexOf(visibleEntry.target);
    if (index !== -1) this._index = index;
  }

  _onVisibilityChange() {
    document.hidden ? this._stopAutoplay() : this._resumeAutoplay();
  }

  _onScroll() {
    window.clearTimeout(this._scrollSettleTimer);
    this._scrollSettleTimer = window.setTimeout(() => this._normalizeLoopPosition(), 120);
  }

  _normalizeLoopPosition() {
    if (!this._loopEnabled) return;
    if (this._index === 0) {
      this._index = this._slides.length - 2;
      this._track.scrollLeft = this._slides[this._index].offsetLeft;
    } else if (this._index === this._slides.length - 1) {
      this._index = 1;
      this._track.scrollLeft = this._slides[this._index].offsetLeft;
    }
  }

  _next() {
    this._goTo(this._index + 1);
  }

  _prev() {
    this._goTo(this._index - 1);
  }

  _goTo(index) {
    if (this._slides.length < 2) return;
    const targetIndex = ((index % this._slides.length) + this._slides.length) % this._slides.length;
    this._index = targetIndex;
    this._track.scrollTo({ left: this._slides[targetIndex].offsetLeft, behavior: this._reducedMotionQuery.matches ? "auto" : "smooth" });
  }

  _resumeAutoplay() {
    if (this._mediaQuery.matches && this._isVisible && !document.hidden) this._startAutoplay();
  }

  _startAutoplay() {
    if (!this.hasAttribute("autoplay") || this._reducedMotionQuery.matches || this._realItems.length < 2) return;
    this._stopAutoplay();
    const seconds = parseFloat(this.getAttribute("autoplay")) || 5;
    this._autoplayInterval = window.setInterval(() => this._next(), seconds * 1e3);
  }

  _stopAutoplay() {
    if (this._autoplayInterval) {
      window.clearInterval(this._autoplayInterval);
      this._autoplayInterval = null;
    }
  }
};
if (!window.customElements.get("fat-announcement-bar")) {
  window.customElements.define("fat-announcement-bar", FatAnnouncementBar);
}

// snippets/fat-header-search.liquid — locale-aware Shopify query and product suggestions.
// The server owns result markup so product images, URLs, money formatting and price privacy remain
// Liquid-driven. This component only coordinates requests, modal state and listbox interaction.
const FatPredictiveSearch = class extends HTMLElement {
  // Short internals keep this globally loaded bundle below its strict 16KB minified budget.
  // _i=input, _p=panel, _rr=result root, _rc=result container, _c=cache, _mq=mobile query.
  connectedCallback() {
    this._f = this.querySelector("[data-search-form]");
    this._i = this.querySelector("[data-search-input]");
    this._cb = this.querySelector("[data-search-clear]");
    this._xb = this.querySelector("[data-search-close]");
    this._bd = this.querySelector("[data-search-backdrop]");
    this._p = this.querySelector("[data-search-panel]");
    this._l = this.querySelector("[data-search-loading]");
    this._rc = this.querySelector("[data-search-results]");
    this._rr = this.querySelector("[data-fat-predictive-results]");
    if (!this._f || !this._i || !this._p || !this._rr) return;

    this._c = new Map();
    this._ai = -1;
    this._io = false;
    this._mq = window.matchMedia("(max-width: 999px)");
    this._ac = new AbortController();
    const signal = this._ac.signal;

    this._i.addEventListener("input", this._oi.bind(this), { signal });
    this._i.addEventListener("focus", () => this._of(), { signal });
    this._i.addEventListener("keydown", this._ik.bind(this), { signal });
    this.addEventListener("keydown", this._sk.bind(this), { signal });
    this.addEventListener("click", this._sg.bind(this), { signal });
    this._f.addEventListener("submit", (event) => this._su(event), { signal });
    this._cb?.addEventListener("click", () => this._cl(), { signal });
    this._xb?.addEventListener("click", () => this._x({ restoreFocus: true }), { signal });
    this._bd?.addEventListener("click", () => this._x({ restoreFocus: true }), { signal });
    document.addEventListener("pointerdown", this._dp.bind(this), { signal });
    this._mq.addEventListener("change", this._vc.bind(this), { signal });

    this._sc();
  }

  disconnectedCallback() {
    this._ac?.abort();
    this._fx?.abort();
    window.clearTimeout(this._st);
    this._rm();
  }

  _q() {
    return this._i.value.trim();
  }

  _k(query) {
    return query.toLocaleLowerCase();
  }

  _oi() {
    const query = this._q();
    this._sc();
    this._sa(-1);
    window.clearTimeout(this._st);
    this._fx?.abort();

    if (!query) {
      this._x();
      return;
    }

    const key = this._k(query);
    if (this._c.has(key)) {
      this._rn(this._c.get(key), query);
      return;
    }

    this._sl();
    this._st = window.setTimeout(() => this._s(query), 280);
  }

  _of() {
    const query = this._q();
    if (!query || this._io) return;
    const key = this._k(query);

    if (this._c.has(key)) {
      this._rn(this._c.get(key), query);
    } else {
      this._sl();
      window.clearTimeout(this._st);
      this._st = window.setTimeout(() => this._s(query), 280);
    }
  }

  // A suggestion re-searches in place instead of navigating. The anchor keeps its href so the
  // pre-upgrade markup still works, and modified clicks still open the results page in a new tab.
  _sg(event) {
    const suggestion = event.target.closest?.("[data-search-suggestion]");
    if (!suggestion) {
      // Reopening on a click, not just on focus: an outside click that dismissed the panel never
      // moved focus off the input, so no second focus event is coming.
      if (event.target === this._i) this._of();
      return;
    }
    if (event.defaultPrevented || event.button > 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const term = (suggestion.dataset.searchSuggestion || "").trim();
    if (!term) return;

    event.preventDefault();
    this._i.value = term;
    this._sc();
    this._sa(-1);
    window.clearTimeout(this._st);
    this._fx?.abort();
    this._i.focus();

    const key = this._k(term);
    if (this._c.has(key)) {
      this._rn(this._c.get(key), term);
      return;
    }
    this._sl();
    this._s(term);
  }

  async _s(query) {
    const key = this._k(query);
    if (key !== this._k(this._q())) return;

    this._fx?.abort();
    this._fx = new AbortController();

    const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const params = new URLSearchParams({
      q: query,
      section_id: this.getAttribute("section-id") || "",
      "resources[type]": "query,product",
      "resources[limit]": "6",
      "resources[limit_scope]": "each",
      "resources[options][fields]": "title,product_type,variants.title,vendor,variants.sku,variants.barcode",
    });

    try {
      const response = await fetch(`${routeRoot}search/suggest?${params}`, {
        signal: this._fx.signal,
      });
      const responseText = await response.text();
      if (!response.ok) throw new Error(String(response.status));
      if (key !== this._k(this._q())) return;

      const responseDocument = new DOMParser().parseFromString(responseText, "text/html");
      const nextResults = responseDocument.querySelector("[data-fat-predictive-results]");
      if (!nextResults) throw new Error("Missing results fragment");

      const markup = nextResults.outerHTML;
      this._c.set(key, markup);
      if (this._c.size > 20) this._c.delete(this._c.keys().next().value);
      this._rn(markup, query);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.warn("fat-predictive-search: request failed", error);
      if (key === this._k(this._q())) this._x();
    }
  }

  _rn(markup, query) {
    const template = document.createElement("template");
    template.innerHTML = markup.trim();
    const nextResults = template.content.querySelector("[data-fat-predictive-results]");
    if (!nextResults) return this._x();

    this._rr.replaceWith(nextResults);
    this._rr = nextResults;
    this._sa(-1);
    this._hl();

    if (this._hr()) {
      this._o();
    } else {
      this._x();
    }
  }

  _hr() {
    return this._rr?.dataset.hasResults === "true";
  }

  _sl() {
    this._l.hidden = false;
    this._rc.hidden = true;
    this._p.setAttribute("aria-busy", "true");
    this._o();
  }

  _hl() {
    this._l.hidden = true;
    this._rc.hidden = false;
    this._p.removeAttribute("aria-busy");
  }

  _o() {
    if (!this._q()) return;
    this._io = true;
    this.dataset.open = "";
    this._p.hidden = false;
    if (this._bd) this._bd.hidden = false;
    if (this._xb) this._xb.hidden = false;
    this._i.setAttribute("aria-expanded", "true");
    this._vc();
  }

  _x({ restoreFocus = false } = {}) {
    this._io = false;
    delete this.dataset.open;
    this._p.hidden = true;
    this._p.removeAttribute("aria-busy");
    if (this._bd) this._bd.hidden = true;
    if (this._xb) this._xb.hidden = true;
    if (this._l) this._l.hidden = true;
    if (this._rc) this._rc.hidden = false;
    this._i.setAttribute("aria-expanded", "false");
    this._sa(-1);
    this._rm();
    if (restoreFocus) {
      // Keep keyboard focus restoration from immediately reopening the cached results.
      this._io = true;
      this._i.focus();
      this._io = false;
    }
  }

  _cl() {
    this._i.value = "";
    this._i.dispatchEvent(new Event("input", { bubbles: true }));
    this._i.focus();
  }

  _sc() {
    if (this._cb) this._cb.hidden = !this._q();
  }

  _os() {
    return this._rr ? Array.from(this._rr.querySelectorAll("[data-search-option]")) : [];
  }

  _sa(index) {
    const options = this._os();
    options.forEach((option) => option.setAttribute("aria-selected", "false"));
    this._ai = index >= 0 && index < options.length ? index : -1;

    if (this._ai === -1) {
      this._i.removeAttribute("aria-activedescendant");
      return;
    }

    const activeOption = options[this._ai];
    activeOption.setAttribute("aria-selected", "true");
    this._i.setAttribute("aria-activedescendant", activeOption.id);
    activeOption.scrollIntoView({ block: "nearest" });
  }

  _ik(event) {
    if (event.key === "Escape" && this._io) {
      event.preventDefault();
      event.stopPropagation();
      this._x({ restoreFocus: true });
      return;
    }

    if (!this._io || !this._hr()) return;
    const options = this._os();
    if (!options.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this._sa((this._ai + 1) % options.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this._sa(this._ai <= 0 ? options.length - 1 : this._ai - 1);
    } else if (event.key === "Enter" && this._ai >= 0) {
      event.preventDefault();
      // Product options wrap a card rather than being the link themselves.
      const option = options[this._ai];
      const link = option.matches("a[href]") ? option : option.querySelector("a[href]");
      (link || option).click();
    }
  }

  _sk(event) {
    if (!this._io || !this._mq.matches || event.key !== "Tab") return;
    const focusable = Array.from(this.querySelectorAll('a[href], button:not([hidden]), input:not([disabled])'))
      .filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  _su(event) {
    if (!this._q()) event.preventDefault();
  }

  _dp(event) {
    if (this._io && !event.composedPath().includes(this)) this._x();
  }

  _vc() {
    if (!this._io) return this._rm();
    if (this._mq.matches) {
      this.setAttribute("role", "dialog");
      this.setAttribute("aria-modal", "true");
      this.setAttribute("aria-label", this._p.getAttribute("aria-label") || "Search");
      document.body.classList.add("fat-predictive-search-open");
    } else {
      this._rm();
    }
  }

  _rm() {
    this.removeAttribute("role");
    this.removeAttribute("aria-modal");
    this.removeAttribute("aria-label");
    document.body.classList.remove("fat-predictive-search-open");
  }
};
if (!window.customElements.get("fat-predictive-search")) {
  window.customElements.define("fat-predictive-search", FatPredictiveSearch);
}

// snippets/fat-header-actions.liquid — the dealer-price privacy switch in the header.
// `adviesprijsShown` stays as the storage key so existing preferences survive the V2 migration.
// Internally the state is always named `pricesHidden`: true hides dealer-only monetary values while
// leaving the consumer advice price and purchase controls available.
const PRICE_VISIBILITY_STORAGE_KEY = "adviesprijsShown";
const PRICE_VISIBILITY_EVENT = "fat:price-visibility-change";

const FatPriceToggle = class extends HTMLElement {
  connectedCallback() {
    this._switch = this.querySelector("[data-switch]");
    if (!this._switch) return;

    this._pricesHidden = this._readStoredState();
    this._applyState(this._pricesHidden, false);

    this._onSwitchClick = this._onSwitchClick.bind(this);
    this._onStorage = this._onStorage.bind(this);

    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this._switch.addEventListener("click", this._onSwitchClick, { signal });
    window.addEventListener("storage", this._onStorage, { signal });
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
  }

  // Blocked/partitioned storage throws rather than returning null, so treat any failure as
  // "prices visible" — the same default as a first-time visitor.
  _readStoredState() {
    try {
      return window.localStorage.getItem(PRICE_VISIBILITY_STORAGE_KEY) === "true";
    } catch (error) {
      return false;
    }
  }

  _onSwitchClick() {
    this._setState(!this._pricesHidden);
  }

  _setState(pricesHidden) {
    if (pricesHidden === this._pricesHidden) return;

    try {
      window.localStorage.setItem(PRICE_VISIBILITY_STORAGE_KEY, String(pricesHidden));
    } catch (error) {
      // Storage is unavailable; the toggle still works for this page view.
    }

    this._applyState(pricesHidden, true);
  }

  _applyState(pricesHidden, announce) {
    this._pricesHidden = pricesHidden;
    document.documentElement.classList.toggle("product-prices-hidden", pricesHidden);
    if (document.body) document.body.classList.toggle("product-prices-hidden", pricesHidden);
    this._switch.setAttribute("aria-checked", pricesHidden ? "false" : "true");

    if (announce) {
      document.dispatchEvent(
        new CustomEvent(PRICE_VISIBILITY_EVENT, {
          detail: { pricesHidden, pricesVisible: !pricesHidden },
        }),
      );
    }
  }

  _onStorage(event) {
    if (event.key !== PRICE_VISIBILITY_STORAGE_KEY) return;
    const pricesHidden = this._readStoredState();
    if (pricesHidden === this._pricesHidden) return;
    this._applyState(pricesHidden, true);
  }
};
if (!window.customElements.get("fat-price-toggle")) {
  window.customElements.define("fat-price-toggle", FatPriceToggle);
}

// assets/fat-cart.js exposes the shared window.FatCart helper used by the components below.

// snippets/fat-product-card.liquid — quantity stepper + add-to-cart.
//
// Pure enhancement: the card is a real <form action="/cart/add">, so it still works if this file
// never loads. Submit is only intercepted when window.FatCart exists, otherwise the browser posts
// the form and lands on /cart.
//
// The number input is the only place the quantity lives — read it, change it, write it back.
// Labels come off data attributes because JS cannot read locales/*.json.
const FatProductCard = class extends HTMLElement {
  connectedCallback() {
    // connectedCallback fires again whenever the card is moved in the DOM, so setup runs once.
    if (this.ready) return;
    this.form = this.querySelector("[data-fat-product-card-form]");
    if (!this.form) return;
    this.ready = true;

    this.input = this.querySelector("[data-qty-input]");
    this.button = this.querySelector("[data-add-button]");
    this.label = this.querySelector("[data-add-label]");
    this.defaultLabel = this.label ? this.label.textContent : "";

    // Every listener sits on this element or its own children, so they are collected along with
    // the card when a grid re-renders. Nothing to unbind.
    this.addEventListener("click", (event) => this.onClick(event));
    this.form.addEventListener("submit", (event) => this.onSubmit(event));
    // Re-writing the typed value through the clamp: a blank field or "0" would otherwise reach
    // cart/add.js and come back as an error the dealer cannot act on.
    if (this.input) this.input.addEventListener("change", () => this.setQuantity(this.getQuantity()));
  }

  // Reads whatever is in the input and floors it at 1 — the input stays the source of truth.
  getQuantity() {
    const parsed = parseInt(this.input ? this.input.value : "1", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  setQuantity(value) {
    if (this.input) this.input.value = String(value);
  }

  onClick(event) {
    if (event.target.closest("[data-qty-decrease]")) {
      this.setQuantity(Math.max(1, this.getQuantity() - 1));
    } else if (event.target.closest("[data-qty-increase]")) {
      this.setQuantity(this.getQuantity() + 1);
    }
  }

  async onSubmit(event) {
    if (!window.FatCart || typeof window.FatCart.addToCart !== "function") return;
    event.preventDefault();
    if (!this.button || this.button.disabled) return;

    const variantInput = this.form.querySelector('input[name="id"]');
    const deliveryInput = this.form.querySelector('input[name="properties[_delivery_time]"]');
    if (!variantInput) return;

    clearTimeout(this.resetTimer);
    this.button.disabled = true;
    this.button.setAttribute("aria-busy", "true");

    try {
      await window.FatCart.addToCart({
        variantId: variantInput.value,
        quantity: this.getQuantity(),
        deliveryTime: deliveryInput ? deliveryInput.value : "",
      });
      this.flashLabel(this.form.dataset.addedLabel);
    } catch (error) {
      this.flashLabel(this.form.dataset.errorLabel);
      console.error("fat-product-card: add to cart failed", error);
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute("aria-busy");
    }
  }

  // Swaps the button label for a moment, then puts the original back.
  flashLabel(text) {
    if (!this.label || !text) return;
    this.label.textContent = text;
    this.resetTimer = setTimeout(() => {
      this.label.textContent = this.defaultLabel;
    }, 1500);
  }
};
if (!window.customElements.get("fat-product-card")) {
  window.customElements.define("fat-product-card", FatProductCard);
}
